import type { NiceModalHandler } from '@ebay/nice-modal-react';
import { FILE_SIZE_LIMIT_BYTES } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  FileUploadControl,
  FileUploadItemsProgress,
  useFileUpload,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useAxios } from '../../hooks/useAxios';

interface Props {
  modal: NiceModalHandler<Record<string, unknown>>;
  url: string;
  files?: File[];
  onUploaded?: (status: 'success' | 'error') => void;
}

export const FileUploadModal = (props: Props) => {
  const { url, onUploaded, modal, files } = props;
  const axios = useAxios();

  const { visible, remove, hide } = modal;

  const sendFile = React.useCallback(
    async (
      file: File,
      onProgress: (opts: { percentage: number; total?: number; loaded?: number }) => void,
    ) => {
      const formData = new FormData();
      formData.append('files', file);

      const fileSizeBeforeUpload = file.size;

      await axios({
        url,
        method: 'POST',
        data: formData,
        onUploadProgress: (event) => {
          if (!event.total) {
            return;
          }

          onProgress({
            percentage: (event.loaded / event.total) * 100,
            total: event.total,
            loaded: event.loaded,
          });
        },
      }).then((res) => {
        const fileSizeAfterUpload = res.data.size;

        if (fileSizeAfterUpload !== fileSizeBeforeUpload) {
          Sentry.captureEvent({
            message: 'File size mismatch after upload',
            extra: {
              fileName: file.name,
              fileSizeBeforeUpload,
              fileSizeAfterUpload,
            },
          });
        }
      });

      onProgress({ percentage: 100 });
    },
    [url, axios],
  );

  const fileUploader = useFileUpload({
    sendFile,
    concurrency: 4,
    autoStart: true,
    maxFileSize: FILE_SIZE_LIMIT_BYTES,
  });

  const { onFilesChange, reset, status, fileProgress } = fileUploader;

  React.useEffect(() => {
    if (files && !P.isEmpty(files)) {
      onFilesChange(files);
    }
  }, [onFilesChange, files]);

  React.useEffect(() => {
    if (status === 'finished') {
      onUploaded?.('success');
    } else if (status === 'errored') {
      onUploaded?.('error');
    }
  }, [status, onUploaded]);

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='lg'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle title='Add files' />
          <ChakraV3.Dialog.Body>
            {P.isEmpty(fileUploader.files) && (
              <FileUploadControl
                multiple={true}
                files={fileUploader.files}
                onFilesChange={onFilesChange}
                status={status}
              />
            )}
            <FileUploadItemsProgress files={Object.values(fileProgress)} />
          </ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.HStack>
              <ChakraV3.Button onClick={reset} size='sm' variant='subtle'>
                Reset
              </ChakraV3.Button>
              <ChakraV3.Button onClick={hide} size='sm' colorPalette='brand'>
                Close
              </ChakraV3.Button>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
};
